import React from 'react'
import clsx from 'clsx'

import { CloseIcon, MicIcon, MicOffIcon, SparkleIcon } from './icons'
import './VoiceAssistantWidget.scss'

export type VoiceAssistantState =
    | 'idle'
    | 'connecting'
    | 'listening'
    | 'thinking'
    | 'speaking'
    | 'error'
    | 'muted'

export type VoiceAssistantInteraction = 'hover' | 'pressed' | 'focused'

export const VOICE_ASSISTANT_STATES: VoiceAssistantState[] = [
    'idle',
    'connecting',
    'listening',
    'thinking',
    'speaking',
    'error',
    'muted',
]

const STATUS_LABELS: Record<VoiceAssistantState, string> = {
    idle: '',
    connecting: 'Connecting…',
    listening: 'Listening',
    thinking: 'Thinking…',
    speaking: 'Speaking',
    error: 'Mic blocked · Retry',
    muted: 'Muted',
}

const ORB_LABELS: Record<VoiceAssistantState, string> = {
    idle: 'Ask the assistant',
    connecting: 'Connecting, tap to cancel',
    listening: 'Listening, tap to stop',
    thinking: 'Thinking, tap to stop',
    speaking: 'Speaking, tap to interrupt',
    error: 'Microphone blocked, tap to retry',
    muted: 'Muted, tap to resume',
}

const SPEAKING_BARS = 5

interface VoiceAssistantWidgetProps {
    state: VoiceAssistantState
    isMuted?: boolean
    onOrbClick?: () => void
    onMuteToggle?: () => void
    onClose?: () => void
    // Forces a pseudo-state so Storybook can preview hover/pressed/focused.
    interaction?: VoiceAssistantInteraction
    className?: string
}

const VoiceAssistantWidget: React.FC<VoiceAssistantWidgetProps> = ({
    state,
    isMuted = false,
    onOrbClick,
    onMuteToggle,
    onClose,
    interaction,
    className,
}) => {
    const isActive = state !== 'idle'

    return (
        <div className={clsx('voice-assistant', `voice-assistant--${state}`, className)}>
            <div className="voice-assistant__row">
                <div
                    className={clsx(
                        'voice-assistant__pill',
                        `voice-assistant__pill--${state}`,
                        isActive && 'voice-assistant__fade--visible',
                    )}
                    role="status"
                    aria-live="polite"
                >
                    {isActive && (
                        <>
                            <span className="voice-assistant__pill-dot" aria-hidden="true" />
                            <span className="voice-assistant__pill-text">
                                {STATUS_LABELS[state]}
                            </span>
                        </>
                    )}
                </div>

                <div
                    className={clsx(
                        'voice-assistant__controls',
                        isActive && 'voice-assistant__fade--visible',
                    )}
                >
                    <button
                        type="button"
                        className="voice-assistant__control"
                        onClick={onMuteToggle}
                        aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                        aria-pressed={isMuted}
                    >
                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </button>
                    <button
                        type="button"
                        className="voice-assistant__control"
                        onClick={onClose}
                        aria-label="End voice session"
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className="voice-assistant__orb-wrap">
                    {state === 'idle' && (
                        <span
                            className={clsx(
                                'voice-assistant__tooltip',
                                interaction === 'hover' && 'voice-assistant__tooltip--visible',
                            )}
                            aria-hidden="true"
                        >
                            Ask the assistant
                        </span>
                    )}
                    <button
                        type="button"
                        className={clsx(
                            'voice-assistant__orb',
                            `voice-assistant__orb--${state}`,
                            interaction && `voice-assistant__orb--is-${interaction}`,
                        )}
                        onClick={onOrbClick}
                        aria-label={ORB_LABELS[state]}
                    >
                        {state === 'connecting' && <span className="voice-assistant__arc" />}
                        {state === 'listening' && (
                            <>
                                <span className="voice-assistant__ring voice-assistant__ring--pulse" />
                                <span className="voice-assistant__ring voice-assistant__ring--pulse voice-assistant__ring--delayed" />
                                <span className="voice-assistant__ring" />
                            </>
                        )}
                        {state === 'thinking' && (
                            <>
                                <span className="voice-assistant__ring voice-assistant__ring--dashed" />
                                <span className="voice-assistant__orbit">
                                    <span className="voice-assistant__orbit-dot" />
                                </span>
                            </>
                        )}
                        {state === 'speaking' && <span className="voice-assistant__ring" />}

                        <span className="voice-assistant__core">
                            {state === 'speaking' && (
                                <span className="voice-assistant__bars">
                                    {Array.from({ length: SPEAKING_BARS }, (_, i) => (
                                        <span key={i} className="voice-assistant__bar" />
                                    ))}
                                </span>
                            )}
                            {state === 'error' && (
                                <MicOffIcon className="voice-assistant__mic-off" />
                            )}
                            {state !== 'speaking' && state !== 'error' && (
                                <SparkleIcon className="voice-assistant__sparkle" />
                            )}
                        </span>

                        {state === 'error' && <span className="voice-assistant__badge">!</span>}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default VoiceAssistantWidget
